import { useCallback, useInsertionEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react'
import { pageOf } from '../lib/keys'
import { useSurface, useTabScope } from './context'
import { MAX_DRAFT_BYTES, MAX_DRAFTS, SETTINGS_BACKGROUND, draftKey, type RecoveryDraft } from './protocol'

const identity = (tab: string, path: string, key: string) => JSON.stringify([tab, path, key])
/** Only explicitly registered editor values are recoverable. Never inspect DOM, transcripts or terminal buffers. */
export class DraftRegistry {
  private readonly entries = new Map<string, RecoveryDraft>()
  private readonly oversized = new Map<string, boolean>()
  get bounded() { return this.oversized.size === 0 && this.entries.size <= MAX_DRAFTS }
  read(tab: string, path: string, key: string) { return this.entries.get(identity(tab, path, key))?.value }
  write(tab: string, path: string, key: string, value?: string, dirty = true) {
    // The workspace's own recovery metadata is never an editor's draft.
    if (!draftKey(key) || key === SETTINGS_BACKGROUND) return false
    const name = identity(tab, path, key)
    const held = this.entries.get(name)
    if (held?.value === value && (held === undefined || held.dirty === dirty) && !this.oversized.has(name)) return false
    this.oversized.delete(name)
    if (value === undefined) this.entries.delete(name)
    else if (new TextEncoder().encode(value).length > MAX_DRAFT_BYTES) { this.entries.delete(name); this.oversized.set(name, dirty) }
    else this.entries.set(name, { tab, path, key, value, dirty })
    return true
  }
  /** Whether a tab holds a dirty draft, or one on these pages. */
  hasDirty(tab: string, pages?: readonly string[]) {
    const counts = (heldTab: string, path: string) => heldTab === tab && (!pages || pages.includes(path))
    return [...this.entries.values()].some(entry => entry.dirty && counts(entry.tab, entry.path))
      || [...this.oversized].some(([key, dirty]) => { const [heldTab, path] = JSON.parse(key); return dirty && counts(heldTab, path) })
  }
  removePath(tab: string, path: string) {
    for (const [name, entry] of this.entries) if (entry.tab === tab && entry.path === path) this.entries.delete(name)
    for (const name of this.oversized.keys()) { const [heldTab, heldPath] = JSON.parse(name); if (heldTab === tab && heldPath === path) this.oversized.delete(name) }
  }
  removeTab(tab: string) {
    for (const [name, entry] of this.entries) if (entry.tab === tab) this.entries.delete(name)
    for (const name of this.oversized.keys()) if (JSON.parse(name)[0] === tab) this.oversized.delete(name)
  }
  clear() { this.entries.clear(); this.oversized.clear() }
  snapshot() { return [...this.entries.values()] }
}

/** A useState-compatible draft, scoped by logical tab, page and an explicit stable editor key.
 * The page is the one the editor is drawn on, so a page kept under Settings keeps its drafts.
 * Browser pages keep ordinary in-memory state. Native recovery is volatile and identity checked.
 * Use a validator for structured values. Saving/cancelling sets the value back to its initial empty state.
 */
export function useWorkspaceDraft<T>(key: string, initial: T | (() => T), valid?: (value: unknown) => value is T, isDirty?: (value: T) => boolean): [T, Dispatch<SetStateAction<T>>] {
  const scope = useTabScope()
  const surface = useSurface()
  const path = scope ? pageOf(surface ?? new URL(scope.path(), 'http://workspace.invalid').pathname) : ''
  const name = JSON.stringify([scope?.id, path, key])
  const seed = useMemo(() => {
    const empty = typeof initial === 'function' ? (initial as () => T)() : initial
    const text = scope?.readDraft(path, key)
    if (text !== undefined) {
      try {
        const value: unknown = JSON.parse(text)
        if (valid ? valid(value) : typeof value === typeof empty && Array.isArray(value) === Array.isArray(empty)) return { name, value: value as T, empty }
      } catch { /* An invalid leaf value is never installed into an editor. */ }
    }
    return { name, value: empty, empty }
    // Initial state is evaluated once per explicit editor identity, as with useState.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name])
  const [held, setHeld] = useState(seed)
  if (held.name !== name) setHeld(seed)
  const value = held.name === name ? held.value : seed.value
  const dirty = isDirty ? isDirty(value) : JSON.stringify(value) !== JSON.stringify(seed.empty)
  useInsertionEffect(() => {
    if (!scope) return
    const text = JSON.stringify(value)
    scope.writeDraft(path, key, text === JSON.stringify(seed.empty) ? undefined : text, dirty)
    return () => scope.writeDraft(path, key, undefined)
  }, [scope, path, key, value, seed, dirty])
  const set = useCallback<Dispatch<SetStateAction<T>>>(next => setHeld(current => ({ ...seed, value: typeof next === 'function' ? (next as (held: T) => T)(current.name === name ? current.value : seed.value) : next })), [seed, name])
  return [value, set]
}
